import { PageHeader } from '@ant-design/pro-layout'
import { useApolloClient, useQuery } from '@apollo/client'
import { FetchViewsDocument, type FetchViewsQuery } from '@salescore/client-api'
import { getOrganizationIdFromPath, isVisualizeLicenseRequiredError } from '@salescore/client-base'
import { HandleQuery, SuspenseWithLoading } from '@salescore/client-common'
import { Divider, Empty, Space } from 'antd'
import { Array } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useState } from 'react'

import {
  attachMetricToViewFieldsFromFormula,
  type ViewFieldsFragmentWithMetric,
} from '../recoil/view/attachMetricToKpiConfig'
import { ViewComponent, type ViewsContext } from './ViewComponent'
import { ViewDataLoading, ViewLoading } from './ViewLoading'
import { VisualizeUserPlanNotFound } from './VisualizeUserPlanNotFound'

interface ViewComponentWrapperProperties {
  onFinish?: (argument?: { id?: string }) => void
  viewId: string
  viewsContext: ViewsContext
  withHeader?: boolean
}

/**
 * view の設定を取得して描画する。
 *
 * 取得の失敗は FetchedViewComponent が投げ、ここで囲った ErrorBoundary が受け取る。
 * ErrorBoundary は子孫のエラーしか受け取れないため、投げる側と囲う側をコンポーネントとして分けている。
 * 囲う位置がこの view の表示領域の内側になるので、呼び出し側の画面 (サイドバー・タブ・ドリルダウン元の view) は
 * エラー表示に差し替わらない。
 */
export const ViewComponentWrapper = (properties: ViewComponentWrapperProperties): ReactElement => (
  <HandleQuery loadingElement={<ViewLoading />}>
    <FetchedViewComponent {...properties} />
  </HandleQuery>
)

const FetchedViewComponent = ({
  onFinish,
  viewId,
  viewsContext,
  withHeader,
}: ViewComponentWrapperProperties): ReactElement => {
  const [views, setViews] = useState<ViewFieldsFragmentWithMetric[]>([])
  const view = views.find((x) => x.id === viewId)
  const client = useApolloClient()
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaveConfigButtonActive, setIsSaveConfigButtonActive] = useState(false)
  const [isChangedDrillDownModal, setIsChangedDrillDownModal] = useState(false)

  const { data, error, loading, refetch } = useQuery(FetchViewsDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
      ids: [viewId],
      withDependency: true,
    },
    // 2023/01/14 cache-firstからno-cacheに変更
    // …が、no-cacheにしていてパフォーマンスの深刻な問題があったためにcache-firstにした気がする。要調整
    fetchPolicy: `no-cache`,
    onCompleted: (data) => {
      setViews(data.views.map(attachMetricToViewFieldsFromFormula))
    },
  })

  // onAddResource needs an ad-hoc fetch for a different set of ids than the main query.
  const fetchViews = async (ids: string[]): Promise<FetchViewsQuery['views']> => {
    const result = await client.query({
      query: FetchViewsDocument,
      variables: {
        organizationId: getOrganizationIdFromPath(),
        ids,
        withDependency: true,
      },
      fetchPolicy: `no-cache`,
    })
    return result.data.views
  }

  if (view === undefined) {
    if (error !== undefined) {
      // ライセンス不足はサーバーが code で返す。設定は取得できないので、案内に出す種別名はサイドバーのメタデータから引く。
      // サイドバーに無い (種別が分からない) ときは汎用の 403 表示に任せる
      if (isVisualizeLicenseRequiredError(error)) {
        const siderView = viewsContext.viewsForSider.find((x) => x.id === viewId)
        if (siderView !== undefined) {
          return <VisualizeUserPlanNotFound viewType={siderView.type} />
        }
      }
      // 認可エラー・通信断を画面に出すため、上位の ErrorBoundary へ委譲する。
      // 取得済みの view が無いときだけ投げるので、保存後の再取得が失敗しても表示中の view は残る
      throw error
    }
    // 取得が終わっているのに結果に対象の view が含まれないときは、待っても表示できない
    if (!loading && data !== undefined && !data.views.some((x) => x.id === viewId)) {
      return <Empty description={t(`ビューが見つかりません`)} />
    }
    return <ViewLoading />
  }
  const body = (
    <SuspenseWithLoading type="custom" loadingElement={<ViewDataLoading view={view} />}>
      <ViewComponent
        parameter={{}}
        onFinish={onFinish}
        view={view}
        parentAbility={undefined}
        viewsContext={{
          ...viewsContext,
          views,
          // onSaveView, onAddResourceなどを作ってしまったが、後からupdateViewをproxyすればいいことに気づいた
          updateView: async (x) => {
            const result = await viewsContext.updateView(x)
            if (result !== undefined) {
              // 依存があればreplaceする
              setViews((oldViews) =>
                oldViews.map((oldView) =>
                  oldView.id === result.id ? attachMetricToViewFieldsFromFormula(result) : oldView,
                ),
              )
            }
            return result
          },
          pickView: (viewId: string) => {
            viewsContext.pickView(viewId)
          },
          refetchViews: async () => {
            await viewsContext.refetchViews()
          },
          onAddResource: async ({ viewIds }) => {
            if (viewIds === undefined || viewIds.length === 0) {
              return
            }
            const fetchedViews = await fetchViews(viewIds)
            await viewsContext.onAddResource({ viewIds })
            setViews((oldViews) => {
              const newViews = Array.dedupeWith(
                [...fetchedViews.map(attachMetricToViewFieldsFromFormula), ...oldViews],
                (a, b) => a.id === b.id,
              )
              return newViews
            })
          },

          onSaveView: async (views, option) => {
            if (option?.forceFetch === true) {
              await refetch()
              return
            }
            // 依存があればreplaceする
            setViews((oldViews) =>
              oldViews.map((oldView) => {
                const newView = views.find((view) => view.id === oldView.id)
                if (newView === undefined) {
                  return oldView
                }
                return attachMetricToViewFieldsFromFormula(newView)
              }),
            )

            // XXX: 以下のときだけrefetchする
            // 1. 名前に変更があったとき
            // 2. KPI設定でシートを変更したとき(=2つ変更されたとき、とする)
            const savedView = views.find((x) => x.id === viewId)
            if ((savedView !== undefined && savedView.name !== view.name) || views.length >= 2) {
              await viewsContext.onSaveView(views)
              await refetch()
            }
          },
          isSaveConfigButtonActive,
          setIsSaveConfigButtonActive,
          isChangedDrillDownModal,
          setIsChangedDrillDownModal,
          hasChanges,
          onChangesChange: (hasChange: boolean) => {
            setHasChanges(hasChange)
          },
        }}
      />
    </SuspenseWithLoading>
  )
  if (withHeader !== true) {
    return body
  }

  // このコンポーネントで、wrap以外のことをしたくなかったが、ヘッダーのロジックをいれるのに適した場所がなかったのでここでやることにする
  return (
    <PageHeader
      title={<Space className="px-4 pt-2">{view.name}</Space>}
      style={{
        padding: 0,
      }}
      className="w-full overflow-hidden"
    >
      <Divider style={{ margin: 0 }} />
      <div
        style={{
          height: 'calc(100vh - 150px)', // 画面一杯に表示するために、100vhからモーダル・タイトルをざっくり引いた値を指定
        }}
      >
        {body}
      </div>
    </PageHeader>
  )
}
